<template>
  <div>
    <div class="content-header">
      <div class="container-fluid">
        <div class="row mb-2">
          <div class="col-sm-6">
            <h1 class="m-0">{{ this.$route.name }}</h1>
          </div>
          <div class="col-sm-6">
            <ol class="breadcrumb float-sm-right">
              <li class="breadcrumb-item active"><i v-bind:class="routeIcon"></i> {{ this.$route.name }}</li>
            </ol>
          </div>
        </div>
      </div>
    </div>

    <section class="content">
      <div class="container-fluid">
        <b-row class="col-lg-12">
          <div class="col-lg-3 col-md-3 col-sm-12" v-for="({id, label, options, loaded, searchChange, internalSearch }) in filterInputs" v-bind:key="id">
            <b-form-group label-cols-lg="3" :label-for="id + '-filter-input'" :label="label">
              <multiselect v-model="filters[id]"
                           :disabled="!loaded"
                           :loading="!loaded"
                           :options="options"
                           label="label"
                           track-by="value"
                           :multiple="false"
                           :placeholder="'Select ' + label"
                           :internal-search="internalSearch"
                           @search-change="searchChange"
              />
            </b-form-group>
          </div>
        </b-row>

        <b-row class="col-lg-12">
          <div class="col-lg-12 col-md-12 col-sm-12 apply-clear">
            <FilterButtons
                :cancel="this.cancel"
                :loadFn="loadData"
                :loadButtonDisabled="!(this.filters.operator && this.filters.table && this.filters.currency)"
                :cancelFn="cancelRequest"
                :resetFn="reset"
                search-title="Check"
            />
          </div>
        </b-row>

        <b-row class="col-lg-12">
          <div class="col-lg-12 col-md-12 col-sm-12 apply-clear">
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header">
                    <h3 class="card-title">Result</h3>
                  </div>
                  <div class="card-body">
                    <template v-if="tableLimits">
                      <p class="limit-result-meta">{{ tableLimits.currency }} / {{ tableLimits.limitName || 'Default' }} / {{ tableLimits.id }}</p>
                      <b-tabs content-class="mt-3">
                        <b-tab title="Published" active><PublishedLimits :published="tableLimits.published" /></b-tab>
                        <b-tab title="Draft">
                          <p>Mode: {{ tableLimits.calculation ? tableLimits.calculation.mode : 'inherit' }}</p>
                          <p v-if="tableLimits.currency !== 'EUR'" class="limit-result-meta">EUR source: {{ tableLimits.calculation && tableLimits.calculation.eurSourceId || 'Matched EUR (same table, operator, limit type)' }}</p>
                          <b-tabs content-class="mt-3">
                            <b-tab title="Normal"><LimitSettingsPreview :settings="tableLimits.settings" /></b-tab>
                            <b-tab v-if="tableLimits.currency === 'EUR'" title="Peak">
                              <LimitSettingsPreview :settings="tableLimits.calculation ? tableLimits.calculation.peakSettings : null" empty-text="No draft peak settings" />
                              <LimitSettingsPreview v-if="tableLimits.calculation && tableLimits.calculation.peakSchedule" :settings="tableLimits.calculation.peakSchedule" />
                            </b-tab>
                          </b-tabs>
                        </b-tab>
                      </b-tabs>
                    </template>
                    <b-alert v-if="error" show variant="danger">{{ error }}</b-alert>
                    <span v-if="loading">Loading..</span>
                    <span v-if="!loading && loaded && tableLimits === null">Table has no limits</span>
                    <span v-if="!loading && !loaded && !error && tableLimits === null">Select operator, table and currency to check limits</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-row>
      </div>
    </section>
  </div>
</template>

<script>
import {getRouteIcon, ROUTE_TABLE_LIMITS} from "../../utils/routerIcons";
import FilterButtons from "../../components/FilterButtons";
import axios from "axios";
import Multiselect from "vue-multiselect";
import PublishedLimits from '../../components/Limits/PublishedLimits.vue';
import LimitSettingsPreview from '../../components/Limits/LimitSettingsPreview.vue';
import { LIMIT_TYPES } from '../../constants/LimitTypes';

function getDefaultFilters() {
  return {
    operator: null,
    table: null,
    currency: null,
    limitType: { value: 0, label: 'Default' },
  };
}

export default {
  components: {
    FilterButtons,
    Multiselect,
    PublishedLimits,
    LimitSettingsPreview,
  },
  data() {
    return {
      tableLimits: null,
      loaded: false,
      loading: false,
      cancel: null,
      error: '',
      requestId: 0,
      filters: getDefaultFilters(),
      operatorOptions: [],
      tableOptions: [],
      currencyOptions: [],
      currenciesQuery: '',
    }
  },
  computed: {
    currencyFilteredOptions() {
      return this.currencyOptions.filter((item) => item.label.toLowerCase().startsWith(this.currenciesQuery.toLowerCase()))
    },
    filterInputs() {
      return [
        {id: 'limitType', label: 'Limit Type', options: LIMIT_TYPES.map(({ value, text }) => ({ value, label: text })), internalSearch: true, loaded: true, searchChange: () => {}},
        {id: 'operator', label: 'Operator', options: this.operatorOptions, internalSearch: true, loaded: this.$store.getters['operatorsForSelect/loaded'], searchChange: () => {}},
        {id: 'table', label: 'Table', options: this.tableOptions, internalSearch: true, loaded: this.$store.getters['tablesForSelect/loaded'], searchChange: () => {} },
        {id: 'currency', label: 'Currency', options: this.currencyFilteredOptions, internalSearch: false, loaded: this.$store.getters['currenciesForSelect/loaded'],
          searchChange: (query) => {this.currenciesQuery = query}
        },
      ];
    },
    routeIcon: () => getRouteIcon(ROUTE_TABLE_LIMITS),
  },
  methods: {
    reset() {
      this.cancelRequest();
      this.filters = getDefaultFilters();
      this.loaded = false;
      this.tableLimits = null;
      this.error = '';
    },
    loadData() {
      this.cancelRequest();
      const requestId = ++this.requestId;
      this.tableLimits = null;
      this.loaded = false;
      this.error = '';

      let self = this;

      this.loading = true;

      return this
          .$http({
            url: '/api/v1/limits',
            params: {
              partnerId: this.filters.operator?.value,
              tableId: this.filters.table?.value,
              currency: this.filters.currency?.value,
              limitType: this.filters.limitType?.value ?? 0,
              limit: 100,
              offset: 0,
            },
            method: 'GET',
            cancelToken: new axios.CancelToken(c => {self.cancel = c})
          })
          .then(resp => {
            if (requestId !== this.requestId) return;
            this.tableLimits = (resp.data.items || []).find(item =>
              Number(item.limitType || 0) === Number(this.filters.limitType?.value ?? 0) &&
              String(item.tableId) === String(this.filters.table?.value) &&
              item.partnerId === this.filters.operator?.value && item.currency === this.filters.currency?.value
            ) || null;
            this.loaded = true;
          })
          .catch((err) => {
            if (requestId !== this.requestId || axios.isCancel(err)) return;
            this.error = err.response?.data?.message || 'Could not load table limits.';
            this.$toastr.e(this.error);
          })
          .finally(() => {
            if (requestId !== this.requestId) return;
            this.cancel = null;
            this.loading = false;
          });
    },
    cancelRequest() {
      this.requestId++;
      if (this.cancel) {
        this.cancel('this -> cancel previous request');
      }
      this.cancel = null;
      this.loading = false;
    },
  },
  beforeDestroy() {
    this.cancelRequest();
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.cancelRequest();
        this.tableLimits = null;
        this.loaded = false;
        this.error = '';
      },
    },
  },
  async beforeMount() {
    await this.$store.dispatch('operatorsForSelect/loadList').then(() => {
      this.operatorOptions = this.$store.getters['operatorsForSelect/list'].map(({label, value}) => {return { label, value }});
    });
    await this.$store.dispatch('currenciesForSelect/loadList').then(() => {
      this.currencyOptions = this.$store.getters['currenciesForSelect/list'].map(({label, value}) => {return { label, value }});
    });
    await this.$store.dispatch('tablesForSelect/loadList').then(() => {
      this.tableOptions = this.$store.getters['tablesForSelect/list'].map(({label, value}) => {return { label, value }});
    });
  },
}
</script>

<style scoped>
.limit-result-meta { overflow-wrap: anywhere; }
</style>
